// ABOUTME: SPEC-34 — pure transform: the carousel variant segments (a split-asset second
// ABOUTME: spec OR the embedded plane of a combined spec) into `variant_slot_N` chapters.
//
// The vendor schema is `{total_frames, sequence: [{id, name, start_frame, end_frame,
// freeze_frame}]}` — N segments that each travel from the shared lineup shot into one
// piece, freeze on it (the PDP hold), and travel back to the lineup. Every segment
// boundary IS the lineup shot (verified §0.2, SSIM 0.981–0.989; re-verified per drop by
// scripts/verify_loop_closure.py's variant-boundary checks), which is what makes slot→slot
// hard cuts invisible — whether those cuts are cross-video (split assets, D3) or seeks
// within one file (combined asset, D26).
//
// Two provenances, one transform (D26):
//   • split assets — the legacy second file (Caroussel.json), fetched separately, frames
//     relative to its OWN media file (the matched Caroussel.mp4);
//   • combined asset — `extractEmbeddedVariantSections` lifts integer-id entries out of
//     the MAIN spec's `sections`, frames absolute in the shared media file. Segments may
//     legitimately be non-contiguous there: the 2026-08-27 combined drop keeps the old
//     ~248-frame slot lattice but tightens each travel to ~148 frames, leaving unused
//     strips the grammar never addresses.
//
// Chapter ids are the namespaced `variant_slot_N` scheme (D2): brand-agnostic, collision-
// proof against SECTION_TO_CHAPTER_ID (a duplicate id is a red-panel failure), and mapped
// to catalogue ids ONLY backend-side (tenant config `reel_carousel.slots`). The vendor's
// `name` fields are footage slang ("Orange watch" = the salmon piece) and must never
// reach voice or UI (E22/D22) — this transform reads only the numbers.
//
// D12 — the shipped JSON's `total_frames: 1735` undercounts the real 1736-frame file by
// one. Tolerated as-is: segments never address past the last declared index, `duration`
// derives from the spec (≤ the real length), and file EOF is covered by the player's
// `ended` backstop.

import { isMainSectionId, effectiveTotalFrames } from './reelSpecTransform';

const VARIANT_SLOT_PREFIX = 'variant_slot_';

export function variantChapterId(position) {
  return `${VARIANT_SLOT_PREFIX}${position}`;
}

export function isVariantChapterId(id) {
  return typeof id === 'string' && id.startsWith(VARIANT_SLOT_PREFIX);
}

/**
 * SPEC-34 D26 — the combined-cut path: a drop that carries both films in ONE file embeds
 * the variant segments in the main spec's `sections` as integer-id entries ({id: 1..N,
 * name, start_frame, end_frame, freeze_frame}, no `action`), frames absolute in the
 * combined file. String ids stay main-reel sections; `isMainSectionId` (not a bare
 * integer test) keeps the '360' trap closed — a future drop shipping 360 as a JSON
 * NUMBER must stay watch_360, never become variant_slot_360.
 *
 * @returns {object|null} A spec shaped exactly like the legacy second file
 *   ({total_frames, sequence}) so buildCarouselChapters transforms either provenance
 *   identically — or null when the spec embeds nothing (a split-asset drop).
 */
export function extractEmbeddedVariantSections(toolSpec) {
  const sections = toolSpec?.sections;
  if (!Array.isArray(sections)) return null;
  const variants = sections.filter((s) => Number.isInteger(s?.id) && !isMainSectionId(s.id));
  if (variants.length === 0) return null;
  // SPEC-61 D-4: share the SAME duration basis as the main plane (effectiveTotalFrames), so
  // a spec that under-declares `total_frames` (F5) doesn't reject a variant segment the main
  // plane's own roll chapter already tolerates.
  return { total_frames: effectiveTotalFrames(toolSpec), sequence: variants };
}

/**
 * Transform the carousel vendor spec into manifest chapters.
 *
 * @param {object} carouselSpec - Raw parsed Caroussel.json.
 * @param {object} options
 * @param {string} options.media - The carousel media filename (resolved against the same
 *   mediaBase as every other chapter — D13; the .json/.mp4 are a MATCHED PAIR).
 * @param {number} [options.fps] - Frame cadence. The caller guards fps equality with the
 *   main spec BEFORE calling (D11) — the player's frame math is single-fps.
 * @returns {object[]} Chapters conforming to the reelSchema.js chapter contract.
 */
export function buildCarouselChapters(carouselSpec, { media, fps = 30 } = {}) {
  if (!media) throw new Error('carouselSpecTransform: media filename is required');
  const totalFrames = carouselSpec?.total_frames;
  if (typeof totalFrames !== 'number' || totalFrames <= 0) {
    throw new Error(`carouselSpecTransform: invalid total_frames: ${totalFrames}`);
  }
  const sequence = [...(carouselSpec.sequence ?? [])].sort((a, b) => a.id - b.id);
  if (sequence.length === 0) {
    throw new Error('carouselSpecTransform: spec has no sequence segments');
  }
  const duration = totalFrames / fps;

  return sequence.map((segment) => {
    const { id, start_frame: startFrame, end_frame: endFrame, freeze_frame: freezeFrame } = segment;
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error(`carouselSpecTransform: segment id must be a positive integer, got ${id}`);
    }
    // The freeze must sit STRICTLY inside its segment — a freeze on a boundary would hold
    // on the lineup shot instead of the piece. The conformance suite pins the shipped
    // asset; this guards the next render against silently breaking the grammar.
    if (!(startFrame >= 0 && startFrame < freezeFrame && freezeFrame < endFrame)) {
      throw new Error(
        `carouselSpecTransform: segment ${id} frames are not ordered `
        + `(start ${startFrame} < freeze ${freezeFrame} < end ${endFrame} required)`,
      );
    }
    if (endFrame > totalFrames) {
      throw new Error(
        `carouselSpecTransform: segment ${id} ends at frame ${endFrame}, past total_frames ${totalFrames}`,
      );
    }
    const chapterId = variantChapterId(id);
    const sStart = startFrame / fps;
    const freezeTime = freezeFrame / fps;
    const sEnd = endFrame / fps;
    return {
      id: chapterId,
      // Never rendered (variant chapters author no captions and suppress point tags, D15)
      // and deliberately NOT the vendor's `name` — footage slang never leaks (E22).
      title: `Piece ${id}`,
      // The catalogue id behind the slot arrives at runtime via the reel_spec_sheet
      // payload (§3.3) — the manifest carries no catalogue knowledge.
      variant: 'shared',
      media,
      duration,
      // Freeze-shaped, exactly the main transform's `freeze` grammar: travel in from the
      // lineup, rest on the piece, travel back out to the lineup on departure.
      segments: [
        { type: 'move', start: sStart, end: freezeTime },
        { type: 'hold', start: freezeTime, end: freezeTime },
        { type: 'exit', start: freezeTime, end: sEnd },
      ],
      beats: [
        { time: sStart, label: `${chapterId}-approach` },
        { time: freezeTime, label: `${chapterId}-hold` },
        { time: sEnd, label: `${chapterId}-neutral` },
      ],
      deixis: {},
      captions: [],
      allowedTransitions: [],
    };
  });
}
